import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    StatusMultiSelectCaret,
    StatusMultiSelectClearButton,
    StatusMultiSelectDropdown,
    StatusMultiSelectFooter,
    StatusMultiSelectLabel,
    StatusMultiSelectOption,
    StatusMultiSelectRoot,
    StatusMultiSelectTrigger,
    StatusMultiSelectTriggerText,
} from './DeliveriesList.styles';

function buildSummary(selectedValues, options) {
    if (!selectedValues?.length) return 'Todos';
    if (selectedValues.length === 1) {
        const match = options.find((opt) => opt.value === selectedValues[0]);
        return match?.label ?? selectedValues[0];
    }
    return `${selectedValues.length} status selecionados`;
}

/**
 * Combobox de múltipla seleção de status de entrega (checkboxes).
 *
 * @param {{
 *   id?: string,
 *   label?: string,
 *   value?: string[],
 *   options: Array<{ value: string, label: string }>,
 *   onChange: (selected: string[]) => void,
 *   disabled?: boolean,
 * }} props
 */
export default function DeliveryStatusMultiSelect({
    id,
    label = 'Status',
    value = [],
    options,
    onChange,
    disabled = false,
}) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const selectedSet = useMemo(() => new Set(value || []), [value]);
    const summary = useMemo(() => buildSummary(value, options), [value, options]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleValue = (statusValue) => {
        const next = new Set(selectedSet);
        if (next.has(statusValue)) {
            next.delete(statusValue);
        } else {
            next.add(statusValue);
        }
        onChange(Array.from(next));
    };

    const handleClear = (event) => {
        event.stopPropagation();
        onChange([]);
    };

    return (
        <StatusMultiSelectRoot ref={containerRef}>
            {label ? (
                <StatusMultiSelectLabel htmlFor={id}>{label}</StatusMultiSelectLabel>
            ) : null}
            <StatusMultiSelectTrigger
                id={id}
                type="button"
                onClick={() => !disabled && setOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={open}
                disabled={disabled}
            >
                <StatusMultiSelectTriggerText title={summary}>
                    {summary}
                </StatusMultiSelectTriggerText>
                <StatusMultiSelectCaret aria-hidden>
                    {open ? '▲' : '▼'}
                </StatusMultiSelectCaret>
            </StatusMultiSelectTrigger>
            {open && !disabled ? (
                <StatusMultiSelectDropdown role="listbox" aria-multiselectable="true" aria-label={label}>
                    {options.map((option) => {
                        const checked = selectedSet.has(option.value);
                        return (
                            <StatusMultiSelectOption
                                key={option.value}
                                onMouseDown={(event) => event.preventDefault()}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleValue(option.value)}
                                />
                                <span>{option.label}</span>
                            </StatusMultiSelectOption>
                        );
                    })}
                    {selectedSet.size > 0 ? (
                        <StatusMultiSelectFooter>
                            <StatusMultiSelectClearButton type="button" onClick={handleClear}>
                                Limpar seleção
                            </StatusMultiSelectClearButton>
                        </StatusMultiSelectFooter>
                    ) : null}
                </StatusMultiSelectDropdown>
            ) : null}
        </StatusMultiSelectRoot>
    );
}
